import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

function DeleteAllIssuesModal({
  isOpen,
  onClose,
  onConfirm,
  deleteAllIssuesData,
  deleteAllIssuesLoading,
  deleteAllIssuesError,
  deleteAllIssuesProgress,
}) {
  const { scan, status, issues } = deleteAllIssuesData;
  const { current, total, success, failed, isComplete } =
    deleteAllIssuesProgress;

  const progressPercentage = total > 0 ? (current / total) * 100 : 0;
  const hasStarted = current > 0 || deleteAllIssuesLoading;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {isComplete ? (
                      failed > 0 ? (
                        <XCircleIcon
                          className="h-6 w-6 text-red-600"
                          aria-hidden="true"
                        />
                      ) : (
                        <CheckCircleIcon
                          className="h-6 w-6 text-green-600"
                          aria-hidden="true"
                        />
                      )
                    ) : (
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="ml-3 w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {isComplete
                        ? failed > 0
                          ? "Deletion Completed with Errors"
                          : "Deletion Completed Successfully"
                        : "Delete All Issues by Status"}
                    </Dialog.Title>

                    <div className="mt-2">
                      {!hasStarted ? (
                        <p className="text-sm text-gray-500">
                          {scan && status
                            ? `Are you sure you want to delete all ${issues.length} "${status}" issues from scan "${scan.title}"? This action cannot be undone.`
                            : "Are you sure you want to delete these issues?"}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-500">
                            {isComplete
                              ? `Completed: ${success} deleted${
                                  failed > 0 ? `, ${failed} failed` : ""
                                } out of ${total} issues`
                              : `Deleting issues... ${current}/${total}`}
                          </p>

                          {/* Progress Bar */}
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                isComplete
                                  ? failed > 0
                                    ? "bg-yellow-500"
                                    : "bg-green-500"
                                  : "bg-blue-500"
                              }`}
                              style={{ width: `${progressPercentage}%` }}
                            ></div>
                          </div>

                          {/* Success/Fail Counts */}
                          <div className="flex justify-between text-xs text-gray-600">
                            <span className="text-green-600">
                              ✓ Success: {success}
                            </span>
                            {failed > 0 && (
                              <span className="text-red-600">
                                ✗ Failed: {failed}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Error Message */}
                      {deleteAllIssuesError && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          {deleteAllIssuesError}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex justify-end space-x-3">
                      {!hasStarted ? (
                        <>
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            onClick={onClose}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            onClick={onConfirm}
                          >
                            Delete All
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={`inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            isComplete
                              ? "bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-500"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          }`}
                          onClick={isComplete ? onClose : undefined}
                          disabled={!isComplete}
                        >
                          {isComplete ? "Close" : "Deleting..."}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default DeleteAllIssuesModal;
